import type { ConstantsAccountExportStruct } from "@gosaas/types";
import {
	IconAlertTriangle,
	IconDownload,
	IconTrash,
} from "@tabler/icons-react";
import { useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/components/providers/auth";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	apiClient,
	requestWithHandling,
	TypedClientApiError,
} from "@/utils/api";
import { suppressNextUnauthorizedRedirectToast } from "@/utils/auth-session";
import { deleteAccessToken, deleteRefreshToken } from "@/utils/local-storage";

/** Builds the toast description for a blocked deletion (sole-Owner teams). */
export function blockedDeletionDescription(blockingTeams: string[]): string {
	const teams = blockingTeams.join(", ");
	return `Transfer ownership of ${teams} (or remove their members) before deleting your account.`;
}

export function extractBlockedTeams(error: unknown): string[] | null {
	if (
		error instanceof TypedClientApiError &&
		error.code === "ACCOUNT_DELETION_BLOCKED"
	) {
		const data = error.data as { blocking_teams?: string[] } | null;
		return data?.blocking_teams ?? [];
	}
	return null;
}

export function DangerZoneCard() {
	const router = useRouter();
	const { user, refresh } = useAuth();
	const [isExporting, setIsExporting] = useState(false);
	const [deleteStep, setDeleteStep] = useState<"closed" | "confirm" | "code">(
		"closed",
	);
	const [deleteCode, setDeleteCode] = useState("");
	const [mfaCode, setMfaCode] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	const exportData = async () => {
		setIsExporting(true);
		try {
			const response = await requestWithHandling<ConstantsAccountExportStruct>(
				() => apiClient.api.userExportList(),
			);
			if (!response.data) {
				throw new Error("Empty export response");
			}
			const file = new Blob([JSON.stringify(response.data, null, 2)], {
				type: "application/json",
			});
			const element = document.createElement("a");
			element.href = URL.createObjectURL(file);
			element.download = `account-export-${new Date().toISOString().slice(0, 10)}.json`;
			document.body.appendChild(element);
			element.click();
			document.body.removeChild(element);
			toast.success("Account data exported", {
				description: "Your data download has started.",
			});
		} catch (error) {
			toast.error("Couldn't export your data", {
				description:
					error instanceof TypedClientApiError
						? error.message
						: "An unknown error occurred.",
			});
		} finally {
			setIsExporting(false);
		}
	};

	const requestDeletion = async () => {
		setIsSubmitting(true);
		try {
			await requestWithHandling<void>(() =>
				apiClient.api.userDeleteRequestCreate(),
			);
			toast.success("Verification code sent", {
				description: "Check your email for a 6-digit confirmation code.",
			});
			setDeleteStep("code");
		} catch (error) {
			const blockedTeams = extractBlockedTeams(error);
			if (blockedTeams) {
				toast.error("You still own teams with other members", {
					description: blockedDeletionDescription(blockedTeams),
				});
				setDeleteStep("closed");
			} else {
				toast.error("Couldn't start account deletion", {
					description:
						error instanceof TypedClientApiError
							? error.message
							: "An unknown error occurred.",
				});
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	const confirmDeletion = async () => {
		setIsSubmitting(true);
		try {
			await requestWithHandling<void>(() =>
				apiClient.api.userDelete({
					token: deleteCode,
					mfa_code: mfaCode || undefined,
				}),
			);
			toast.success("Your account is being deleted", {
				description: "We're sorry to see you go.",
			});
			suppressNextUnauthorizedRedirectToast();
			deleteAccessToken();
			deleteRefreshToken();
			localStorage.removeItem("teamID");
			await refresh();
			router.navigate({ to: "/" });
		} catch (error) {
			const blockedTeams = extractBlockedTeams(error);
			if (blockedTeams) {
				toast.error("You still own teams with other members", {
					description: blockedDeletionDescription(blockedTeams),
				});
				setDeleteStep("closed");
			} else {
				toast.error("Couldn't delete your account", {
					description:
						error instanceof TypedClientApiError
							? error.message
							: "An unknown error occurred.",
				});
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Card className="shadow-sm border-destructive/30">
			<CardHeader className="pb-4">
				<div className="flex items-center gap-3">
					<div className="p-2 bg-destructive/10 rounded-lg">
						<IconAlertTriangle
							className="h-5 w-5 text-destructive"
							aria-hidden
						/>
					</div>
					<div>
						<CardTitle className="text-xl">Data & Privacy</CardTitle>
						<CardDescription className="mt-1">
							Export a copy of your data, or permanently delete your account
						</CardDescription>
					</div>
				</div>
			</CardHeader>
			<CardContent className="pt-0 space-y-4">
				<div className="flex items-center justify-between gap-4 rounded-lg border p-4">
					<div>
						<p className="font-medium">Export account data</p>
						<p className="text-sm text-muted-foreground">
							Download a JSON file with your profile, teams, activity, and
							feedback (GDPR data portability).
						</p>
					</div>
					<Button variant="outline" onClick={exportData} disabled={isExporting}>
						<IconDownload className="h-4 w-4 mr-1" aria-hidden />
						{isExporting ? "Exporting…" : "Export"}
					</Button>
				</div>

				<div className="flex items-center justify-between gap-4 rounded-lg border border-destructive/30 p-4">
					<div>
						<p className="font-medium">Delete account</p>
						<p className="text-sm text-muted-foreground">
							Permanently delete your account, personal teams, and all
							associated data. This cannot be undone.
						</p>
					</div>
					<Button
						variant="destructive"
						onClick={() => setDeleteStep("confirm")}
					>
						<IconTrash className="h-4 w-4 mr-1" aria-hidden />
						Delete
					</Button>
				</div>

				<Dialog
					open={deleteStep !== "closed"}
					onOpenChange={(open) => {
						if (!open) {
							setDeleteStep("closed");
							setDeleteCode("");
							setMfaCode("");
						}
					}}
				>
					<DialogContent className="sm:max-w-md">
						{deleteStep === "confirm" && (
							<>
								<DialogHeader>
									<DialogTitle>Delete your account?</DialogTitle>
									<DialogDescription>
										This permanently deletes your account, your personal teams,
										active subscriptions, and all associated data. Teams where
										you are the only owner but other members remain must be
										transferred first. We'll email you a confirmation code to
										continue.
									</DialogDescription>
								</DialogHeader>
								<DialogFooter>
									<Button
										variant="outline"
										onClick={() => setDeleteStep("closed")}
									>
										Cancel
									</Button>
									<Button
										variant="destructive"
										onClick={requestDeletion}
										disabled={isSubmitting}
									>
										{isSubmitting ? "Sending code…" : "Email me a code"}
									</Button>
								</DialogFooter>
							</>
						)}
						{deleteStep === "code" && (
							<>
								<DialogHeader>
									<DialogTitle>Confirm account deletion</DialogTitle>
									<DialogDescription>
										Enter the 6-digit code we sent to your email
										{user?.mfaEnabled
											? " and a code from your authenticator app"
											: ""}
										. This action is irreversible.
									</DialogDescription>
								</DialogHeader>
								<div className="space-y-4">
									<div className="space-y-2">
										<Label htmlFor="delete-code">Email verification code</Label>
										<Input
											id="delete-code"
											inputMode="numeric"
											maxLength={6}
											placeholder="123456"
											value={deleteCode}
											onChange={(e) => setDeleteCode(e.target.value)}
										/>
									</div>
									{user?.mfaEnabled && (
										<div className="space-y-2">
											<Label htmlFor="delete-mfa-code">
												Authenticator code
											</Label>
											<Input
												id="delete-mfa-code"
												inputMode="numeric"
												maxLength={6}
												placeholder="123456"
												value={mfaCode}
												onChange={(e) => setMfaCode(e.target.value)}
											/>
										</div>
									)}
								</div>
								<DialogFooter>
									<Button
										variant="outline"
										onClick={() => setDeleteStep("closed")}
									>
										Cancel
									</Button>
									<Button
										variant="destructive"
										onClick={confirmDeletion}
										disabled={
											isSubmitting ||
											deleteCode.length !== 6 ||
											(user?.mfaEnabled === true && mfaCode.length !== 6)
										}
									>
										{isSubmitting ? "Deleting…" : "Permanently delete"}
									</Button>
								</DialogFooter>
							</>
						)}
					</DialogContent>
				</Dialog>
			</CardContent>
		</Card>
	);
}
